import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { FuseConfirmDialogComponent } from "@fuse/components/confirm-dialog/confirm-dialog.component";
import { SnackBarService } from "cw-utils";
import { evaluate } from "mathjs";
import {
    debounceTime,
    distinctUntilChanged,
    from,
    Subject,
    switchMap,
    takeUntil,
} from "rxjs";
import { Expression } from "../../../../../../../../shared/cw-backend/models/operational/tools/expression/expression";
import { ExpressionProvider } from "../../../../../../../../shared/cw-backend/providers/operational/tools/expression/expression.provider";

@Component({
    selector: "app-calculate-formulas",
    templateUrl: "./calculate-formulas.component.html",
    styleUrls: ["./calculate-formulas.component.scss"],
})
export class CalculateFormulasComponent implements OnInit, OnDestroy {
    expressionList: Expression[];
    expressionItem: Expression;
    calculatorGroup: FormGroup;
    formControls = {};
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    isCalculated: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject();
    private isCalculating = false;

    constructor(
        private _matDialog: MatDialog,
        private _formBuilder: FormBuilder,
        private expressionProvider: ExpressionProvider,
        public dialogRef: MatDialogRef<CalculateFormulasComponent>,
        private snackBar: SnackBarService,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.expressionItem = new Expression();
    }

    ngOnInit() {
        console.log(this.data);
        this.getExpressionList();
    }

    ngOnDestroy(): void {
        if (this.calculatorGroup && !this.isCalculated) {
            this.runCalculateAndSave();
        }

        this.data = null;
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    getExpressionList() {
        this.expressionProvider
            .getExpressionAndParentList(this.data.documentModel)
            .then((res) => {
                this.expressionList = res.map((exp) => {
                    exp.expressionItem = exp.expressionItem.map(
                        (item, index) => {
                            if (
                                item.placementOrder === null ||
                                item.placementOrder === undefined
                            ) {
                                item.placementOrder = index;
                            }

                            if (item.dataType === "select" && item.options) {
                                try {
                                    item.options =
                                        typeof item.options === "string"
                                            ? JSON.parse(item.options)
                                            : item.options;
                                } catch {
                                    item.options = [];
                                }
                            }

                            return item;
                        },
                    );
                    return exp;
                });
                this.expressionItem = this.expressionList[0];
                this.createFormCalculator();
            });
    }

    createFormCalculator() {
        this.formControls = {};

        this.orderedExpressionItems.forEach((form) => {
            let initialValue: any = "";
            if (form.dataType === "number") {
                initialValue = 0;
            }
            this.formControls[form.name] = [initialValue];
        });

        this.calculatorGroup = this._formBuilder.group(this.formControls);

        this.calculatorGroup.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(400),
                distinctUntilChanged(
                    (prev, curr) =>
                        JSON.stringify(prev) === JSON.stringify(curr),
                ),
                switchMap(() => from(this.runCalculateAndSave())),
            )
            .subscribe();

        this.orderedExpressionItems.forEach((form) => {
            this.expressionProvider
                .checkExpressionExists(form.name, this.data.document)
                .then((res) => {
                    if (res?.data !== null && res?.data !== undefined) {
                        let value = res.data;
                        if (form.dataType === "select") {
                            try {
                                value = JSON.parse(res.data);
                            } catch {
                                value = res.data;
                            }
                        }
                        let finalValue = value;

                        if (form.dataType === "date" && value) {
                            finalValue = this.parseDateBR(value);
                        }

                        this.calculatorGroup.controls[form.name].setValue(
                            finalValue,
                            {
                                emitEvent: false,
                            },
                        );
                    }
                });
        });
    }

    parseDateBR(dateStr: string): Date | null {
        if (!dateStr) return null;

        const parts = dateStr.split("/");

        if (parts.length !== 3) return null;

        const day = Number(parts[0]);
        const month = Number(parts[1]) - 1;
        const year = Number(parts[2]);

        return new Date(year, month, day);
    }

    async runCalculateAndSave(): Promise<boolean> {
        if (this.isCalculating) return false;
        this.isCalculating = true;

        const rawValues = this.calculatorGroup.getRawValue();
        let context: any = {};

        try {
            this.orderedExpressionItems.forEach((f) => {
                const val = rawValues[f.name];

                if (f.type !== "R") {
                    if (f.dataType === "date" || f.dataType === "time") {
                        return;
                    }

                    context[`${f.name}__raw`] = val;

                    if (f.dataType === "select") {
                        if (Array.isArray(val)) {
                            context[f.name] = val.length;
                        } else {
                            context[f.name] = Number(val) || 0;
                        }
                    } else if (f.dataType === "number") {
                        context[f.name] =
                            val !== null && val !== undefined && val !== ""
                                ? Number(String(val).replace(",", "."))
                                : 0;
                    }
                } else if (f.dataType === "date") {
                    context[f.name] = val ? this.formatDateBR(val) : "";
                } else if (f.dataType === "time") {
                    context[f.name] = val ? String(val) : "";
                } else {
                    context[f.name] = val !== null ? String(val) : "";
                }
            });

            const results = this.sortByDependency(
                this.orderedExpressionItems.filter((i) => i.type === "R"),
            );

            results.forEach((item) => {
                if (!item.expression) return;

                try {
                    const valorCalculado = evaluate(item.expression, context);

                    context[item.name] = valorCalculado;

                    const valorFormatado =
                        valorCalculado !== null &&
                        !isNaN(valorCalculado) &&
                        isFinite(valorCalculado)
                            ? Number(valorCalculado).toFixed(2)
                            : "0.00";

                    this.calculatorGroup
                        .get(item.name)
                        ?.setValue(valorFormatado, { emitEvent: false });
                } catch (calcError) {
                    console.warn(
                        `Cálculo da variável ${item.name} incompleto.`,
                    );
                }
            });

            const documentExpressionList = this.orderedExpressionItems
                .map((form) => {
                    let val = context.hasOwnProperty(form.name)
                        ? context[form.name]
                        : rawValues[form.name];

                    let finalValue = null;

                    if (
                        val !== null &&
                        val !== undefined &&
                        String(val).trim() !== ""
                    ) {
                        if (form.dataType === "number") {
                            const n = Number(String(val).replace(",", "."));
                            finalValue =
                                !isNaN(n) && isFinite(n)
                                    ? n.toFixed(2)
                                    : "0.00";
                        } else if (form.dataType === "select") {
                            const raw = context[`${form.name}__raw`];

                            if (Array.isArray(raw)) {
                                finalValue = JSON.stringify(raw);
                            } else {
                                finalValue =
                                    raw !== null &&
                                    raw !== undefined &&
                                    raw !== ""
                                        ? String(raw)
                                        : null;
                            }
                        } else if (form.dataType === "date") {
                            finalValue = this.formatDateBR(val);
                        } else if (form.dataType === "time") {
                            finalValue = val ? String(val) : null;
                        } else {
                            finalValue = String(val);
                        }
                    }

                    return {
                        expression: form.id,
                        value: finalValue,
                        documentId: this.data.document,
                    };
                })
                .filter(
                    (item) =>
                        item.value !== null &&
                        item.value !== "" &&
                        item.value !== "NaN",
                );

            if (documentExpressionList.length > 0) {
                await this.expressionProvider.insertDocumentExpression(
                    documentExpressionList,
                );
                this.isCalculated = true;
            }

            this.isCalculating = false;
            return true;
        } catch (e) {
            console.error("Erro no processamento/salvamento:", e);

            this.saveOnlyInputs(rawValues);

            this.isCalculating = false;
            return true;
        }
    }

    formatDateBR(date: any): string {
        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}/${month}/${year}`;
    }

    saveOnlyInputs(rawValues: any) {
        const list = this.orderedExpressionItems
            .filter((f) => f.type !== "R")
            .map((form) => ({
                expression: form.id,
                value:
                    rawValues[form.name] !== null
                        ? String(rawValues[form.name])
                        : null,
                documentId: this.data.document,
            }))
            .filter((item) => item.value !== null && item.value !== "");

        if (list.length > 0) {
            this.expressionProvider.insertDocumentExpression(list);
        }
    }

    calculate() {
        this.runCalculateAndSave();
        this.snackBar.successMessage("Documento atualizado!");
    }

    replaceWithValues(expression: string, context: any): string {
        if (!expression) return "";

        let modifiedString = expression.replace(
            /\b[a-zA-Z_]\w*\b/g,
            (match) => {
                return context.hasOwnProperty(match)
                    ? String(context[match])
                    : match;
            },
        );

        return modifiedString.replace(/(\d+),(\d+)/g, "$1.$2");
    }

    saveData(documentExpressionList, expression) {
        this.expressionProvider
            .insertDocumentExpression(documentExpressionList)
            .then((res) => {
                if (expression) {
                    this.expressionItem = expression;
                    this.createFormCalculator();
                }

                this.isCalculated = true;
            });
    }

    closeModal() {
        this.confirmDialogRef = this._matDialog.open(
            FuseConfirmDialogComponent,
            {
                disableClose: false,
            },
        );

        this.confirmDialogRef.componentInstance.action =
            "Deseja finalizar formulário do Documento?";

        this.confirmDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.dialogRef.close();
            }
        });
    }

    getFlex(size: number): string {
        if (!size) return "0 0 100%";

        return `0 0 ${size}%`;
    }

    hasFilledFields(): boolean {
        const data = this.calculatorGroup?.getRawValue();

        if (!data) return false;

        return Object.keys(data).some((key) => {
            if (key === "expression") return false;
            return data[key] !== null && data[key] !== "";
        });
    }

    changeExpression(expression) {
        if (this.calculatorGroup && !this.isCalculated) {
            this.runCalculateAndSave();
        }

        this.expressionItem = expression;
        this.formControls = {};
        this.isCalculated = false;
        this.createFormCalculator();
    }

    calculateAndChange(expression) {
        this.calculate();

        this.changeExpression(expression);
    }

    async getExpressionItem(expression: Expression) {
        if (this.expressionItem && expression.id === this.expressionItem.id) {
            return;
        }

        if (this.calculatorGroup) {
            const salvouOK = await this.runCalculateAndSave();

            if (!salvouOK) {
                this.snackBar.errorMessage(
                    "Não foi possível salvar os dados atuais. Verifique sua conexão.",
                );
                return;
            }
        }

        this.changeExpression(expression);
    }

    runAutoCalculate() {
        const rawValues = this.calculatorGroup.getRawValue();
        let context: any = {};

        this.orderedExpressionItems.forEach((f) => {
            if (f.type !== "R") {
                if (f.dataType === "date" || f.dataType === "time") {
                    return;
                }
                const value = rawValues[f.name];
                if (f.dataType === "select") {
                    if (Array.isArray(value)) {
                        context[f.name] = value.length;
                    } else {
                        context[f.name] = Number(value) || 0;
                    }
                } else {
                    context[f.name] =
                        value !== null && value !== undefined && value !== ""
                            ? Number(String(value).replace(",", "."))
                            : 0;
                }
            }
        });

        try {
            const results = this.sortByDependency(
                this.orderedExpressionItems.filter((i) => i.type === "R"),
            );

            results.forEach((item) => {
                if (!item.expression) return;

                const exprFinal = this.replaceWithValues(
                    item.expression,
                    context,
                );
                const valorCalculado = evaluate(exprFinal);

                context[item.name] = valorCalculado;

                const valorFormatado =
                    valorCalculado !== null &&
                    !isNaN(valorCalculado) &&
                    isFinite(valorCalculado)
                        ? Number(valorCalculado).toFixed(2)
                        : "0.00";

                this.calculatorGroup
                    .get(item.name)
                    ?.setValue(valorFormatado, { emitEvent: false });
            });

            this.isCalculated = false;
        } catch (e) {
            console.log("Aguardando valores válidos para calcular...");
        }
    }

    getDependencies(expression: string): string[] {
        if (!expression) return [];

        const tokens = expression.match(/\b[a-zA-Z_]\w*\b/g) || [];

        const functions = [
            "sqrt",
            "log",
            "ln",
            "sin",
            "cos",
            "tan",
            "abs",
            "pi",
            "e",
        ];

        return tokens.filter((t) => !functions.includes(t));
    }

    sortByDependency(items: any[]): any[] {
        const sorted: any[] = [];
        const visiting = new Set<string>();
        const visited = new Set<string>();

        const visit = (item: any) => {
            if (visited.has(item.name)) return;

            if (visiting.has(item.name)) {
                throw new Error(
                    `Dependência circular detectada em: ${item.name}`,
                );
            }

            visiting.add(item.name);

            const deps = this.getDependencies(item.expression);

            deps.forEach((dep) => {
                const depItem = items.find((i) => i.name === dep);
                if (depItem) visit(depItem);
            });

            visiting.delete(item.name);
            visited.add(item.name);
            sorted.push(item);
        };

        items.forEach((item) => visit(item));

        return sorted;
    }

    get orderedExpressionItems() {
        return [...(this.expressionItem?.expressionItem || [])].sort((a, b) => {
            const orderA = a.placementOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.placementOrder ?? Number.MAX_SAFE_INTEGER;

            if (orderA !== orderB) {
                return orderA - orderB;
            }

            return (a.id || 0) - (b.id || 0);
        });
    }
}
